import { requestCodecReport } from "codec-http-integration";
import { useSetAtom } from "jotai";
import { Edit, Info, Link, Pause, Plus, SlidersHorizontal, Unlink2 } from "lucide-react";
import { useState } from "react";
import { CodekInfoModal, Modal, PauseCodekModal, useToast } from "@/components";
import AdvanceCodekConfig from "@/components/AdvanceCodecConfigModal";
import LICDebugModal from "@/components/CodekInfoModal";
import ConfirmModal from "@/components/ConfirmModal";
import TestLICCommunicationButton from "@/components/TestLICCommunicationButton";
import Button from "@/components/ui/Button";
import { SYNC_SEQUENCE, useSyncLIC } from "@/hooks/useSyncLIC";
import { createDeviceMessageRequestAtom } from "@/store/crud";
import { appConfig } from "@/utils/config";
import { getDeviceModelLabel } from "@/utils/device-model";
import type { DeviceWithMapping } from "@/utils/mesh-device-utils";
import { formatMappingDate, getDeviceIcon } from "@/utils/mesh-device-utils";
import SelectDeviceForLICModal from "./SelectDeviceForLICModal";

interface ManageNetworkModalProps {
  isOpen: boolean;
  onClose: () => void;
  licDevice: DeviceWithMapping;
  availableDevices?: DeviceWithMapping[]; // Unmapped mesh devices
  onAddDevices?: (
    items: Array<{
      meshDeviceId: string;
      licDeviceId: string;
      startDate: string;
    }>
  ) => void;
  onRemoveDevice?: (deviceId: string) => void;
  onEditLIC?: (licDevice: DeviceWithMapping) => void;
}

function SyncDeviceButton({ licDeviceId }: { licDeviceId: string }) {
  const { status, currentIndex, isRunning, start, stop, reset } = useSyncLIC({
    deviceId: licDeviceId,
    interval: 3000,
  });

  const buttonClick = () => {
    console.log("SyncDeviceButton clicked: ", {
      status,
      isRunning,
      licDeviceId,
    });
    if (isRunning) {
      stop();
      reset();
    } else {
      start();
    }
  };

  return (
    <div className="flex flex-col w-full">
      <Button type="button" variant="info" onClick={buttonClick} className="py-2! min-h-6! grow" size="lg">
        {isRunning ? `Parar` : "Sincronizar CODEK (internet)"}
      </Button>
      {isRunning && (
        <div className="text-md text-gray-700 text-center">
          [{currentIndex}/{SYNC_SEQUENCE.length}] {status} enviado
        </div>
      )}
    </div>
  );
}

function ManageNetworkModal({
  isOpen,
  onClose,
  licDevice,
  availableDevices = [],
  onAddDevices,
  onRemoveDevice,
  onEditLIC,
}: ManageNetworkModalProps) {
  const { showWarning, showSuccess } = useToast();
  const [isSelectDeviceModalOpen, setIsSelectDeviceModalOpen] = useState(false);
  const [deviceIdToRemove, setDeviceIdToRemove] = useState<string | null>(null);

  const createDeviceMessageRequest = useSetAtom(createDeviceMessageRequestAtom);

  const meshDevices = licDevice.meshDevices || [];
  const deviceIcon = getDeviceIcon(licDevice.device.model.id);
  const modelLabel = getDeviceModelLabel(licDevice.device.model.id);

  // Filter out devices already connected to this LIC
  const devicesNotInCurrentNetwork = availableDevices.filter(
    (device) => !meshDevices.some((meshDevice) => meshDevice.id === device.id)
  );

  const handleRemoveDevice = (deviceId: string) => {
    // Open confirmation modal before removing
    setDeviceIdToRemove(deviceId);
  };

  const handleAddDevice = () => {
    if (devicesNotInCurrentNetwork.length === 0) {
      showWarning({
        message: "Não há dispositivos disponíveis para mapear.",
      });
      return;
    }
    setIsSelectDeviceModalOpen(true);
  };

  const handleSelectDevices = (
    items: Array<{
      meshDeviceId: string;
      licDeviceId: string;
      startDate: string;
    }>
  ) => {
    if (onAddDevices) {
      onAddDevices(items);
    }
    setIsSelectDeviceModalOpen(false);
  };

  const handleEditLIC = () => {
    if (onEditLIC && licDevice) {
      onEditLIC(licDevice);
    } else {
      showWarning({
        message: "Funcionalidade de edição ainda não implementada.",
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Gerenciar Rede - ${licDevice.device.identifier}`} size="md">
      <div className="ManageNetworkModal space-y-6">
        {/* LIC Device Info */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <span className="text-xl">{deviceIcon}</span>
              <span className="font-medium text-gray-900">{licDevice.device.identifier}</span>
            </div>
            <Button
              type="button"
              variant="primary"
              onClick={handleEditLIC}
              className="py-2! min-h-6!"
              size="sm"
              title="Editar LIC"
              icon={<Edit size={16} />}
            >
              Editar
            </Button>
          </div>
          <p className="text-sm text-gray-600 mb-1">{modelLabel}</p>
          <div className="flex items-center gap-1 text-sm text-green-600">
            <Link size={14} />
            <span>
              Coordenador para {meshDevices.length} dispositivo
              {meshDevices.length !== 1 ? "s" : ""}
            </span>
          </div>
        </div>

        {/* Connected Devices */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Dispositivos Conectados ({meshDevices.length})</h3>

          {meshDevices.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <div className="text-4xl mb-2">🔗</div>
              <p>Nenhum dispositivo conectado</p>
              <p className="text-sm">Adicione dispositivos para formar uma rede mesh</p>
            </div>
          ) : (
            <div className="space-y-3">
              {meshDevices.map((meshDevice) => (
                <div key={meshDevice.id} className="bg-white border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-gray-400 font-mono text-sm">├─</span>
                        <span className="text-lg">{getDeviceIcon(meshDevice.device.model.id)}</span>
                        <span className="font-medium text-gray-900">{meshDevice.device.identifier}</span>
                        <span className="text-sm text-gray-500">({meshDevice.device.model.id})</span>
                      </div>
                      <p className="text-sm text-gray-600 ml-6">{getDeviceModelLabel(meshDevice.device.model)}</p>
                      {meshDevice.current_mesh_device_mapping && (
                        <p className="text-xs text-gray-500 ml-6">
                          Mapeado desde: {formatMappingDate(meshDevice.current_mesh_device_mapping.start_date)}
                        </p>
                      )}
                    </div>

                    {/* Remove Button */}
                    <Button
                      variant="destructive"
                      onClick={() => handleRemoveDevice(meshDevice.id)}
                      className="p-0! w-8 h-8 rounded-full!"
                      size="sm"
                      title="Remover da rede"
                      icon={<Link size={16} />}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Add Device Button */}
        <Button
          variant="primary"
          onClick={handleAddDevice}
          className="w-full "
          icon={<Plus className="h-5 w-5" />}
          size="md"
        >
          Adicionar Dispositivo
        </Button>

        {/* Action Buttons */}
        <div className="flex flex-col gap-3 pt-4 border-t border-gray-200">
          <TestLICCommunicationButton variant="info" device={licDevice.device} />
          <SyncDeviceButton licDeviceId={licDevice.device.id} />

          <AdvanceCodekConfig
            device={licDevice.device as any}
            trigger={(open) => (
              <Button
                variant="secondary"
                size="lg"
                className="py-2! min-h-6! grow"
                icon={<SlidersHorizontal className="h-4 w-4" />}
                onClick={open}
              >
                Configuração Avançada do Codek
              </Button>
            )}
          />

          <PauseCodekModal
            device={licDevice.device as any}
            trigger={(open) => (
              <Button
                variant="warning"
                size="lg"
                className="py-2! min-h-6! grow"
                icon={<Pause className="h-4 w-4" />}
                onClick={open}
              >
                Pausar / Retomar CODEK
              </Button>
            )}
          />

          <LICDebugModal
            device={licDevice.device as any}
            enablePolling={false}
            trigger={(open) => (
              <Button variant="info" onClick={open} icon={<Info className="h-4 w-4" />}>
                Dados de Depuração CODEK
              </Button>
            )}
          />

          <Button variant="destructive" outline className="flex-1" onClick={onClose}>
            Fechar
          </Button>
        </div>
      </div>

      {/* Select Device Modal */}
      <SelectDeviceForLICModal
        isOpen={isSelectDeviceModalOpen}
        onClose={() => setIsSelectDeviceModalOpen(false)}
        licDevice={licDevice}
        availableDevices={devicesNotInCurrentNetwork}
        onSelectDevices={handleSelectDevices}
      />

      {/* Confirm Remove Device Modal */}
      <ConfirmModal
        isOpen={!!deviceIdToRemove}
        onClose={() => setDeviceIdToRemove(null)}
        onConfirm={() => {
          if (deviceIdToRemove) {
            if (onRemoveDevice) {
              onRemoveDevice(deviceIdToRemove);
            } else {
              showWarning({
                message: "Funcionalidade de remoção ainda não implementada.",
              });
            }
          }
          setDeviceIdToRemove(null);
        }}
        title="Desvincular dispositivo do CODEK?"
        confirmText="Remover"
        cancelText="Cancelar"
        variant="danger"
      >
        <p className="text-gray-700">{`Tem certeza que deseja remover ${
          (licDevice.meshDevices || []).find((d) => d.id === deviceIdToRemove)?.device.identifier || "este dispositivo"
        } da rede?`}</p>
        <p className="text-gray-500 text-sm mt-2">
          Esta ação não removerá o dispositivo da propriedade, apenas o desvinculará do{" "}
          <b>CODEK {licDevice.device.identifier}</b>.
        </p>
      </ConfirmModal>
    </Modal>
  );
}

export default ManageNetworkModal;
